/**
 * Small "Papier de nuit" UI primitives shared across pages — flat fills,
 * solid offset shadows, generous radius. Kept in one file since v1 has a
 * handful of screens and no design-system tooling yet.
 */
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";

/**
 * Colonne de page. `wide` (défaut) prend toute la largeur d'une tablette en
 * portrait (768–1024 px) avec des marges qui grandissent un peu : l'ancien
 * `max-w-md` laissait une colonne de 448 px au milieu d'une tablette Android
 * de 600–767 px (Fabien, 2026-11-03). Le plafond `max-w-5xl` ne mord qu'au
 * bureau. Les grilles de couvertures gagnent des colonnes plutôt que des
 * cartes géantes ; les paragraphes longs se bornent eux-mêmes (`max-w-prose`).
 * `reading` borne les pages de texte suivi (CGV, confidentialité) à
 * l'ancienne largeur tablette ; `narrow` garde les écrans-formulaires
 * centrés (connexion, code) étroits.
 */
export function Container({
  children,
  className = "",
  width = "wide",
}: {
  children: ReactNode;
  className?: string;
  width?: "wide" | "reading" | "narrow";
}) {
  const max = { wide: "max-w-5xl", reading: "max-w-3xl", narrow: "max-w-md" }[width];
  return (
    <div className={`mx-auto w-full ${max} px-4 sm:px-6 md:px-8 ${className}`}>{children}</div>
  );
}

export function PageLayout({ children, className = "" }: { children: ReactNode; className?: string }) {
  // `page-shell` (index.css) handles both insets: the notch at the top and
  // the fixed bottom chrome, whose height follows whether the mini player
  // is on screen. A fixed `pb-24` used to hide the last button of a page.
  return <div className={`page-shell min-h-screen bg-night text-paper ${className}`}>{children}</div>;
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return <p className="eyebrow">{children}</p>;
}

export function Headline({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <h1 className={`headline ${className}`}>{children}</h1>;
}

export function MutedText({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <p className={`text-violet-soft text-sm font-light leading-relaxed ${className}`}>{children}</p>;
}

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`bg-surface border border-line rounded-card overprint ink-line p-4 ${className}`}
    >
      {children}
    </div>
  );
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "ghost" | "coral";
}

/**
 * La surimpression porte la hiérarchie : plus l'encre de doublage contraste
 * avec le fond nuit, plus le bouton appelle. `primary` (jaune doublé corail)
 * est le seul à crier ; `secondary` se double dans l'encre des filets, donc
 * se voit sans rivaliser. `ghost` n'est pas doublé du tout — sans aplat de
 * fond, l'encre décalée apparaîtrait *à travers* le bouton au lieu de
 * derrière lui ; il garde un simple retrait à l'appui.
 */
export function Button({ variant = "primary", className = "", ...props }: ButtonProps) {
  const base =
    "tap-target inline-flex items-center justify-center gap-2 rounded-card font-heading font-semibold text-sm px-5 py-3 disabled:opacity-50 disabled:pointer-events-none";
  const variants: Record<string, string> = {
    primary: "bg-sun text-mint-ink overprint ink-coral",
    secondary: "bg-surface text-paper border border-line overprint ink-line",
    ghost: "bg-transparent text-violet-soft border border-line transition-opacity active:opacity-60",
    coral: "bg-coral text-mint-ink overprint ink-sun",
  };
  return <button className={`${base} ${variants[variant]} ${className}`} {...props} />;
}

export function IconButton({ className = "", ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      className={`tap-target inline-flex items-center justify-center rounded-full bg-surface border border-line text-paper overprint overprint-sm ink-line ${className}`}
      {...props}
    />
  );
}

/**
 * Retour en haut d'une page de détail.
 *
 * La flèche porte son libellé : seule, elle demande de deviner (Fabien,
 * 2026-09-22). Reste dessinée comme un `IconButton` — pastille de surface,
 * surimpression serrée dans l'encre des filets — pour qu'elle n'entre pas en
 * concurrence avec le bouton « Écouter » juste en dessous.
 *
 * `self-start` est porté ici plutôt que laissé à l'appelant : les pages de
 * détail sont des colonnes flex, où un bouton sans lui s'étire sur toute la
 * largeur et lit comme un bandeau. StoryDetail et PackDetail l'avaient
 * oublié, là où LegalPage et ChouettePlus le posaient à la main.
 */
export function BackButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className="tap-target self-start inline-flex items-center gap-2 rounded-pill bg-surface border border-line text-paper font-heading font-semibold text-sm pl-3 pr-4 overprint overprint-sm ink-line"
    >
      <ArrowLeft size={18} aria-hidden />
      {t("common.back")}
    </button>
  );
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className="w-full min-h-[44px] rounded-card bg-surface border border-line px-4 py-3 text-paper placeholder:text-violet-muted font-body text-base focus:outline-none focus:ring-2 focus:ring-sun"
      {...props}
    />
  );
}

/** Multi-line sibling of `Input` — same "Papier de nuit" surface, resize
 *  disabled (the caller sizes it with `rows`) so a stray drag can't stretch
 *  the card layout. */
export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className="w-full rounded-card bg-surface border border-line px-4 py-3 text-paper placeholder:text-violet-muted font-body text-base focus:outline-none focus:ring-2 focus:ring-sun resize-none"
      {...props}
    />
  );
}

/** Discreet character counter under a free-text field (`Input`/`Textarea`
 *  with a `maxLength`) — never scolding, just a plain "12/150". */
export function CharCounter({ length, max }: { length: number; max: number }) {
  return <p className="mt-1 text-right text-xs text-violet-muted">{length}/{max}</p>;
}

export function Label({ children }: { children: ReactNode }) {
  return <label className="block text-xs font-heading font-semibold uppercase tracking-wide text-violet-soft mb-1.5">{children}</label>;
}

export function ErrorBanner({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-card border border-coral bg-coral/10 text-coral text-sm px-4 py-3 font-body">
      {children}
    </div>
  );
}

export function SeedTag({ children, variant = "sun" }: { children: ReactNode; variant?: "sun" | "mint" }) {
  // Clamp lives on the inner `seed-tag__text`, not on this pill itself —
  // see the comment on `.seed-tag` in index.css for why.
  return (
    <span className={`seed-tag ${variant === "mint" ? "seed-tag--mint" : ""}`}>
      <span className="seed-tag__text">{children}</span>
    </span>
  );
}

/**
 * Progress bar for long-running jobs (generation screen). Deliberately
 * shows no digits and no ETA (Fabien, 2026-09-20) — width is the only
 * visible signal. The caller (Generation.tsx) owns the monotonic/"creep"
 * animation and the `aria-live` step announcement; this component just
 * paints whatever fraction it's given and carries the ARIA progressbar
 * role for assistive tech.
 */
export function ProgressBar({ value, label }: { value: number; label: string }) {
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      className="w-full h-3 rounded-pill bg-surface border border-line shadow-solid-sm overflow-hidden"
    >
      <div
        className="h-full rounded-pill bg-mint motion-safe:transition-[width] motion-safe:duration-700 motion-safe:ease-out"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export function Pill({ children, active = false, onClick }: { children: ReactNode; active?: boolean; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`tap-target rounded-pill px-4 py-2 text-sm font-heading font-semibold border overprint overprint-sm ${
        active
          ? "bg-sun text-mint-ink border-sun ink-coral"
          : "bg-surface text-violet-soft border-line ink-line"
      }`}
    >
      {children}
    </button>
  );
}

/** Admin view only: when a free story families can't see yet goes live
 *  (`date` already formatted), or "not scheduled" without a date. Same
 *  footprint as `NewBadge` so the two can share the cover's corner. */
export function ScheduledBadge({ date, className = "" }: { date: string | null; className?: string }) {
  const { t } = useTranslation();
  return (
    <span
      className={`rounded-pill bg-night/85 border border-line text-violet-soft text-[10px] font-heading font-semibold px-2 py-0.5 ${className}`}
    >
      {date ? t("catalog.scheduledOn", { date }) : t("catalog.notScheduled")}
    </span>
  );
}

/** Admin view only: a pack retired from the catalogue (`is_active: false`). */
export function HiddenPackBadge({ className = "" }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <span
      className={`rounded-pill bg-night/85 border border-line text-violet-soft text-[10px] font-heading font-semibold px-2 py-0.5 ${className}`}
    >
      {t("catalog.hiddenPack")}
    </span>
  );
}

/** Small "just published" flag — overlaid on a cover (Home's Nouveautés row)
 *  or inline next to a title (Catalogue's filtered grid). Drip publication
 *  (2026-09-22): mirrors the API's `is_new` on a `StoryCard`. */
export function NewBadge({ className = "" }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <span
      className={`rounded-pill bg-coral text-mint-ink text-[10px] font-heading font-semibold uppercase tracking-wide px-2 py-0.5 ${className}`}
    >
      {t("common.newBadge")}
    </span>
  );
}
